import { usePagesPilotFeatureFlags } from '@Hooks/dataFetching/usePagesPilotFeatureFlags';
import { ColumnType } from '@Hooks/useOrdering';
import { AUTH_LEVEL_DEPARTMENT, useTskContext } from '@Hooks/useTskContext';
import { EmployeeOverviewTab } from '@Page/EmployeeOverview/EmployeeOverview';
import { Settings, TableColumnSetting, TableColumnsSetting } from '@tlx/astro-shared';
import { Button, DownloadIcon, Spinner } from '@tlx/atlas';
import { IntlShape, useIntl } from 'react-intl';

export const EMPLOYEE_OVERVIEW_TABLE_COLUMNS_SETTING_KEY = 'employee-overview-table-columns';

export const EMPLOYEE_OVERVIEW_SALARY_TABLE_COLUMNS_SETTING_KEY = 'employee-overview-salary-table-columns';

export const EMPLOYEE_OVERVIEW_TAXCARD_TABLE_COLUMNS_SETTING_KEY = 'employee-overview-taxcard-table-columns';
export const getTabColumnKey = (tab: EmployeeOverviewTab) => {
    switch (tab) {
        case 'salary':
            return EMPLOYEE_OVERVIEW_SALARY_TABLE_COLUMNS_SETTING_KEY;
        case 'taxcard':
            return EMPLOYEE_OVERVIEW_TAXCARD_TABLE_COLUMNS_SETTING_KEY;
        default:
            return EMPLOYEE_OVERVIEW_TABLE_COLUMNS_SETTING_KEY;
    }
};

export const useGetDefaultColumns = (
    intl: IntlShape,
    showDepartment: boolean,
    tab: EmployeeOverviewTab,
    authLevelEmployeeOrWage: number,
): ColumnType[] => {
    const columns = useGetColumns(intl, showDepartment, tab, authLevelEmployeeOrWage);
    return columns.filter((column) => !column.defaultDisabled);
};

export const useGetColumns = (
    intl: IntlShape,
    showDepartment: boolean,
    tab: EmployeeOverviewTab,
    authLevelEmployeeOrWage: number,
): ColumnType[] => {
    const { data } = usePagesPilotFeatureFlags();
    const { formatMessage } = intl;
    const defaultColumns: ColumnType[] = [{ id: 'NUMBER', label: formatMessage({ id: 'th_number_short' }) }];

    //wanted to have it before department, thats why its not in the switch statement
    if (tab === 'salary') {
        defaultColumns.push({
            id: 'NATIONAL_IDENTITY_NUMBER',
            label: formatMessage({ id: 'text_social_security_number_short' }),
            defaultDisabled: true,
        });
    }
    if (showDepartment) {
        defaultColumns.push({
            id: 'DEPARTMENT',
            label: formatMessage({ id: 'text_department' }),
        });
    }
    switch (tab) {
        case 'salary':
            defaultColumns.push(
                {
                    id: 'DIVISION',
                    label: formatMessage({ id: 'text_division' }),
                },
                {
                    id: 'STARTDATE',
                    label: formatMessage({ id: 'text_date_of_employment' }),
                },
                {
                    id: 'REMUNERATION_TYPE',
                    label: formatMessage({
                        id: 'text_salary_type',
                    }),
                    width: '150px',
                    defaultDisabled: true,
                },
                {
                    id: 'EMPLOYMENT_TYPE',
                    label: formatMessage({
                        id: 'text_type_of_employment',
                    }),
                    width: '150px',
                    defaultDisabled: true,
                },
                {
                    id: 'EMPLOYMENT_FORM',
                    label: formatMessage({
                        id: 'text_form_of_employment',
                    }),
                    width: '150px',
                    defaultDisabled: true,
                },
                {
                    id: 'PERCENT_OF_EMPLOYMENT',
                    label: formatMessage({
                        id: 'tsk_text_percent_of_employment_short',
                    }),
                },
                {
                    id: 'ANNUAL_SALARY',
                    label: formatMessage({ id: 'th_yearly_wages' }),
                },
                {
                    id: 'MONTHLY_SALARY',
                    label: formatMessage({ id: 'text_monthly_wage' }),
                },
                {
                    id: 'HOURLY_WAGE',
                    label: formatMessage({ id: 'text_hourly_wage' }),
                },
                {
                    id: 'DELIVERY_METHOD_WAGE_SLIP',
                    label: formatMessage({ id: 'text_payslip_to' }),
                },
                {
                    id: 'BANK_ACCOUNT_NUMBER',
                    label: formatMessage({ id: 'text_bank_account' }),
                },
                {
                    id: 'MUNICIPALITY_ENI',
                    label: formatMessage({
                        id: 'tsk_payroll_tax_municipality',
                    }),
                    defaultDisabled: true,
                },
                {
                    id: 'OCCUPATION_CODE',
                    label: formatMessage({ id: 'text_profession_code' }),
                    defaultDisabled: true,
                },
            );
            break;
        case 'taxcard':
            defaultColumns.push(
                {
                    id: 'STATUS',
                    label: formatMessage({ id: 'text_status' }),
                },
                {
                    id: 'DEDUCTION_CODE',
                    label: formatMessage({
                        id: 'text_advance_tax_decution_deduction_code',
                    }),
                },
                {
                    id: 'TYPE',
                    label: formatMessage({
                        id: 'text_type',
                    }),
                },
                {
                    id: 'TABLE_NUMBER',
                    label: formatMessage({
                        id: 'text_advance_tax_decution_table_number',
                    }),
                },
                {
                    id: 'PERCENTAGE_RATE',
                    label: formatMessage({ id: 'text_percentage' }),
                },
                {
                    id: 'FREE_CARD_AMOUNT',
                    label: formatMessage({
                        id: 'text_advance_tax_decution_free_amount',
                    }),
                },
                {
                    id: 'ADDITIONAL_INFO',
                    label: formatMessage({
                        id: 'text_additional_info',
                    }),
                },
                {
                    id: 'LAST_CHANGED',
                    label: formatMessage({ id: 'text_last_edited' }),
                },
            );
            break;

        default:
            defaultColumns.push(
                {
                    id: 'EMAIL',
                    label: formatMessage({ id: 'text_email' }),
                },
                { id: 'PHONE', label: formatMessage({ id: 'text_phone' }) },
                {
                    id: 'PHONE_WORK',
                    label: formatMessage({ id: 'text_phone_number_work' }),
                },
                {
                    id: 'ADDRESS',
                    label: formatMessage({ id: 'text_address' }),
                },
            );
            if (authLevelEmployeeOrWage >= AUTH_LEVEL_DEPARTMENT) {
                defaultColumns.push(
                    {
                        id: 'DATE_OF_BIRTH',
                        label: formatMessage({ id: 'text_date_of_birth' }),
                    },
                    {
                        id: 'ALLOW_LOGIN',
                        label: formatMessage({ id: 'text_login_access' }),
                        defaultDisabled: true,
                    },
                    {
                        id: 'RATE',
                        label: formatMessage({ id: 'text_hour_rate' }),
                        defaultDisabled: true,
                    },
                    {
                        id: 'HOUR_COST_RATE',
                        label: formatMessage({ id: 'text_hour_cost' }),
                        defaultDisabled: true,
                    },
                    {
                        id: 'USER_ADMINISTRATOR',
                        label: formatMessage({ id: 'text_user_is_admin' }),
                        width: '1fr',
                        defaultDisabled: true,
                    },
                    {
                        id: 'VISMA_2FA_CONNECT',
                        label: formatMessage({ id: 'text_2fa' }),
                        width: '1fr',
                        defaultDisabled: true,
                    },
                    {
                        id: 'ALLOW_REG_INFO',
                        label: formatMessage({
                            id: 'text_allow_registration_on_user',
                        }),
                        width: '150px',
                        defaultDisabled: true,
                    },
                );
                if (data?.value.Attestation) {
                    defaultColumns.push({
                        id: 'EMPLOYEE_APPROVER',
                        label: formatMessage({ id: 'text_employee_attestant' }),
                        defaultDisabled: true,
                    });
                }
            }
            break;
    }

    return defaultColumns;
};

export function EmployeeOverviewTableSettings({
    tab,
    handleDownload,
    pendingDownload,
}: {
    tab: EmployeeOverviewTab;
    handleDownload: () => void;
    pendingDownload: boolean;
}) {
    const intl = useIntl();
    const { hasDepartment, showDepartmentIsEmployeeAdmin, authLevelEmployeeOrWage } = useTskContext();

    const showDepartment = tab == 'salary' || tab == 'taxcard' ? showDepartmentIsEmployeeAdmin : hasDepartment;
    const defaultColumns = useGetDefaultColumns(intl, showDepartment, tab, authLevelEmployeeOrWage);
    const columns = useGetColumns(intl, showDepartment, tab, authLevelEmployeeOrWage);

    return (
        <div className={'atl-mr-8 atl-flex'}>
            <Button
                variant="icon"
                title={intl.formatMessage({ id: 'text_download' })}
                aria-label={intl.formatMessage({ id: 'text_download' })}
                data-testid={'export-employee-table'}
                onClick={handleDownload}
                disabled={pendingDownload}
            >
                {pendingDownload ? <Spinner size={'small'} /> : <DownloadIcon />}
            </Button>
            <Settings>
                <TableColumnsSetting
                    name={getTabColumnKey(tab)}
                    label="Columns"
                    aria-label="Columns"
                    defaultColumns={defaultColumns.map((column) => column.id)}
                >
                    {columns.map((column) => (
                        <TableColumnSetting key={column.id} value={column.id}>
                            {column.label}
                        </TableColumnSetting>
                    ))}
                </TableColumnsSetting>
            </Settings>
        </div>
    );
}
